import { createStore, createEvent, createEffect, combine } from 'effector';
import update from 'immutability-helper';
import request from '@helpers/request';
import api from '@constants/endpoints';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import createPaginationStore from './pagination';
import { AxiosResponse } from 'axios';
import { getMentionsPageSize } from '@constants/pagination';

import IComment from '@interfaces/IComment';
import { block as blockComment } from './comments';

export const reset = createEvent('Mentions Official Account Comment store reset');

export const PAGE_SIZE_STORAGE_KEY = 'mentionsOfficialAccountCommentPageSize';

const defaultFilters = {
  needChecked: false,
};

export const refresh = createEvent<{ filters?: Record<string, any> }>(
  'Mentions OA Comment refresh',
);
export const init = createEvent('Mentions OA Comment init');

const { filters, setFilters } = createFiltersStore({
  listName: 'mentionsOfficialAccountComment',
  deaultValues: defaultFilters,
  resetEvent: reset,
});
export { setFilters };

type SkipUntilDatePaginationParams = {
  pageSize?: number;
  lastDate?: string | null;
};

type GetListParams = { filters?: Record<string, any>; pagination?: SkipUntilDatePaginationParams };

const getPageSize = (pageSize?: number) =>
  getMentionsPageSize(localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || pageSize);

const getCommentsPage = (result: AxiosResponse<any>): IComment[] => {
  const comments = result.data?.body?.comments;
  return Array.isArray(comments) ? comments : comments?.page ?? [];
};

const getCommentMentionDate = (comment?: IComment) =>
  comment?.mentionUpdatedAt || comment?.whenCommented || null;

export const getList = createEffect<GetListParams | void, AxiosResponse<any>>({
  name: 'getList',
  async handler(params) {
    const p: GetListParams = params || {};
    const paginationParams = p.pagination || {};

    let filtersParams = filters.getState();
    if (p.filters) {
      setFilters(p.filters);
      filtersParams = p.filters;
      paginationParams.lastDate = null;
    }

    const { pageSize, lastDate = null } = paginationParams;

    const response: AxiosResponse<any> = await request(api.mentionsOfficialAccount.getComment, {
      data: {
        includeChecked: !!filtersParams.needChecked,
        pagination: {
          type: 'SKIP_UNTIL_DATE',
          pageSize: getPageSize(pageSize),
          lastDate,
        },
      },
    });

    return response;
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'mentionsOfficialAccountComment',
  resetEvent: reset,
  bodyPath: ['data', 'body', 'comments'],
});

export { setPagination };

export const checkAllComments = createEffect<void, AxiosResponse<any>>({
  name: 'checkAllComments',
  async handler() {
    return request(api.mentionsOfficialAccount.checkAllComments);
  },
});

export const checkComment = createEffect<
  { commentId: IComment['id']; preloaderId?: string },
  AxiosResponse<any>
>({
  name: 'checkComment',
  async handler({ commentId }) {
    return request(api.mentionsOfficialAccount.checkComment, {
      urlParams: { commentId },
    });
  },
});

export const replyToComment = createEffect<
  { commentId: string; text: string; replyAccountId: string; images?: File[] },
  AxiosResponse<any>
>({
  name: 'replyToComment',
  async handler(params) {
    const formData = new FormData();
    (params.images || []).forEach((file) => formData.append('images', file));

    return request(api.mentionsOfficialAccount.replyToComment, {
      urlParams: { commentId: params.commentId },
      params: { text: params.text, replyAccountId: params.replyAccountId },
      data: formData,
    });
  },
});

const preloaders = createPreloadersStore([
  getList,
  checkComment,
  checkAllComments,
  replyToComment,
  blockComment,
]);

export const updateListHandler = (
  state: IComment[],
  { params: commentId }: { params: IComment['id'] },
) => {
  const index = state.findIndex((c) => c.id === commentId);
  return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
};

const list = createStore<IComment[]>([])
  .on(getList.done, (state, { params, result }) => {
    const page = getCommentsPage(result);
    const filteredPage = page.filter((comment) => !comment.author?.isOfficial);

    return params && params.pagination?.lastDate ? [...state, ...filteredPage] : filteredPage;
  })
  .on(checkAllComments.done, (state) => {
    const filtersParams = filters.getState();
    return filtersParams.needChecked ? state : [];
  })
  .on(checkComment.done, (state, { params }) => {
    const commentId = params.commentId;
    const index = state.findIndex((c) => String(c.id) === String(commentId));
    if (filters.getState().needChecked && index >= 0) {
      return update(state, { [index]: { $merge: { isChecked: true } } });
    }
    return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
  })
  .on(replyToComment.done, (state, { params }) => {
    const commentId = params.commentId;
    const index = state.findIndex((c) => String(c.id) === String(commentId));
    if (filters.getState().needChecked && index >= 0) {
      return update(state, { [index]: { $merge: { isChecked: true } } });
    }
    return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
  })
  .on(blockComment.done, (state, { params }) => {
    const commentId = params.commentId;
    const index = state.findIndex((c) => String(c.id) === String(commentId));
    return index >= 0 ? update(state, { $splice: [[index, 1]] }) : state;
  })
  .reset(reset);

const hasMore = createStore(true)
  .on(getList.done, (state, { params, result }) => {
    const page = getCommentsPage(result);
    const pageSize = getPageSize(params ? params.pagination?.pageSize : undefined);
    return page.length >= pageSize && !!getCommentMentionDate(page[page.length - 1]);
  })
  .reset(reset);

const nextLastDate = createStore<string | null>(null)
  .on(getList.done, (state, { result }) => {
    const page = getCommentsPage(result);
    return getCommentMentionDate(page[page.length - 1]);
  })
  .reset(reset);

init.watch(() => {
  getList();
});

refresh.watch((params) => {
  getList({ filters: params.filters });
});

const mentionsOfficialAccountComment = combine({
  preloaders,
  list,
  hasMore,
  nextLastDate,
  filters,
  pagination,
  setPagination,
});
export default mentionsOfficialAccountComment;
